import { StyleSheet, View, ViewProps } from 'react-native'
import { CompanyData, DataWithUpload, Draft, useCompany } from 'zens-store'
import { observer } from 'mobx-react'
import { Button, Checkbox, Color, Spacing, Text } from 'zens-storybook'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Link } from 'zens-router'

import { NavigationConfirm } from '../navigation'

import VitalityIcon from './vitalityicon'

const styles = StyleSheet.create({
  container: {},
  content: {
    flexDirection: 'row',
  },
  main: {
    flex: 1,
  },
  right: {
    marginLeft: Spacing.M,
  },
  buttons: {
    marginTop: Spacing.L,
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  text: {
    marginTop: Spacing.S,
    color: Color.metalGrey,
  },
  field: {
    marginTop: Spacing.M,
    flexDirection: 'row',
  },
  skeletonField: {
    marginTop: Spacing.M,
  },
  skeletonDescription: {
    marginTop: Spacing.XS,
  },
})

interface Props extends ViewProps {
  companyId: number
  onSave?: (draft: Draft<DataWithUpload<CompanyData>>) => Promise<void>
  loading?: boolean
}

const CompanyVitalityForm = (props: Props) => {
  const { companyId, onSave, loading, style, ...otherProps } = props
  const company = useCompany({ companyId })
  const { t } = useTranslation()
  const draft = useMemo(() => new Draft<DataWithUpload<CompanyData>>({ id: 0, ...company.data?.data }), [company.data])

  function update(value: boolean) {
    draft.update('vitality_trigger', value)
  }

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <View style={styles.main}>
          <Text level={3}>
            <Trans>Want to improve workplace vitality?</Trans>
          </Text>
          <Text style={styles.text}>
            <Trans>
              Enabling the Zens Vitality mode will make the Led-ring on your PuK devices blink in green for 2 minutes
              when it notices you have been at your desk for a while. This blinking reminds you to to take another
              stance, have a mini break and recharge. When activated, this mode applies to all devices in your company.
            </Trans>
          </Text>
          <View style={styles.field}>
            <Checkbox
              label={t('Vitality trigger active')}
              value={draft.data.vitality_trigger}
              onValueChange={update}
              testID="CompanyVitalityForm.Checkbox"
            />
          </View>
        </View>
        <View style={styles.right}>
          <VitalityIcon width={145} height={145} value={draft.data.vitality_trigger} />
        </View>
      </View>
      <View style={styles.buttons}>
        <Button
          title={t('Save')}
          size="L"
          style={styles.button}
          onPress={() => onSave?.(draft)}
          disabled={loading}
          testID="CompanyVitalityForm.Save"
        />
        <Link href={`/companies/${companyId}/detail`} asChild>
          <Button
            title={t('Cancel')}
            light
            size="L"
            style={styles.button}
            disabled={loading}
            testID="CompanyVitalityForm.Cancel"
          />
        </Link>
      </View>
      <NavigationConfirm draft={draft} />
    </View>
  )
}
interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <View style={styles.main}>
          <Text.Skeleton level={3} />
          <Text.Skeleton style={[styles.text, styles.skeletonDescription]} numberOfLines={2} />
          <Text.Skeleton level={5} style={styles.skeletonField} />
        </View>
        <View style={styles.right}>
          <VitalityIcon width={145} height={145} />
        </View>
      </View>
      <View style={styles.buttons}>
        <Button.Skeleton size="L" style={styles.button} />
        <Button title={t('Cancel')} size="L" style={styles.button} />
      </View>
    </View>
  )
}
CompanyVitalityForm.Skeleton = Skeleton

export default observer(CompanyVitalityForm)
